// Authentication utilities
import { diag } from './diag';

const ACCESS_TOKEN_KEY = 'aviotrack_access_token';
const REFRESH_TOKEN_KEY = 'aviotrack_refresh_token';
const USER_INFO_KEY = 'aviotrack_user_info';
const TOKEN_EXPIRY_KEY = 'aviotrack_token_expiry';

/**
 * Save authentication tokens and user info to localStorage
 * @param {string} accessToken - JWT access token
 * @param {string} refreshToken - JWT refresh token
 * @param {object} userInfo - User information (name, email, userId)
 * @param {string} expiryTimeUTC - Token expiry time (optional, from API response)
 */
export function saveUserSession(accessToken, refreshToken, userInfo, expiryTimeUTC = null) {
  if (accessToken && refreshToken && userInfo) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
    if (expiryTimeUTC) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTimeUTC);
    }
  }
}

/**
 * Get access token from localStorage
 * @returns {string|null}
 */
export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Get refresh token from localStorage
 * @returns {string|null}
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Get user info from localStorage
 * @returns {object|null}
 */
export function getUserInfo() {
  const userInfoStr = localStorage.getItem(USER_INFO_KEY);
  if (!userInfoStr) return null;

  try {
    return JSON.parse(userInfoStr);
  } catch (e) {
    console.error('Error parsing user info:', e);
    return null;
  }
}

/**
 * Get token expiry time from localStorage
 * @returns {string|null}
 */
export function getTokenExpiry() {
  return localStorage.getItem(TOKEN_EXPIRY_KEY);
}

/**
 * Check if user is logged in (has valid access token)
 * @returns {boolean}
 */
export function isLoggedIn() {
  return !!getAccessToken();
}

/**
 * Clear user session (logout)
 */
export function clearUserSession() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_INFO_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
}

// Message left behind for the login screen to show after a forced logout.
// Deliberately NOT cleared by clearUserSession — it has to outlive the session
// wipe and the page replace that follow it.
const LOGOUT_NOTICE_KEY = 'aviotrack_logout_notice';

/**
 * Read and clear the pending forced-logout message, if any. Returns null when
 * the user arrived at the login screen normally (signed out, first visit).
 */
export function consumeLogoutNotice() {
  try {
    const notice = localStorage.getItem(LOGOUT_NOTICE_KEY);
    if (notice) localStorage.removeItem(LOGOUT_NOTICE_KEY);
    return notice || null;
  } catch (_) {
    return null;
  }
}

/**
 * Hard logout: clear local session and reload the page so React mounts in the
 * logged-out state (which shows the login screen). Called from the HTTP /
 * WebSocket layers when token refresh fails, so the user is bounced to the
 * login screen instead of seeing a cascade of 401-disguised-as-CORS errors.
 *
 * Leaves a message for the login screen to display. Without one this is a bare
 * page-replace: the user completes a login (Face ID included), lands back on
 * the login screen and is told nothing at all, which is indistinguishable from
 * the app randomly failing.
 *
 * Idempotent — guarded so multiple in-flight failures can't trigger multiple
 * reloads or wipe a fresh session on the way back in.
 */
let _forcingLogout = false;
export function forceLogoutAndRedirect(reason) {
  if (_forcingLogout) return;
  _forcingLogout = true;
  try {
    // eslint-disable-next-line no-console
    console.warn('[auth] Forcing logout:', reason || '(no reason given)');
  } catch (_) { /* ignore */ }
  try { diag('FORCE_LOGOUT', { reason: reason || null }); } catch (_) { /* ignore */ }
  clearUserSession();
  // Set AFTER clearUserSession so the wipe can never race it away.
  try {
    localStorage.setItem(LOGOUT_NOTICE_KEY,
      'Your session ended and you were signed out. Please sign in again.');
  } catch (_) { /* quota/full — the redirect still happens */ }
  // Reload to the root so the React app mounts unauthenticated and the
  // login screen takes over. Using replace so the broken page is not in
  // history (prevents the user "going back" into the broken state).
  try {
    window.location.replace('/');
  } catch (_) {
    // Test / non-browser environments: fall through quietly.
  }
}

// Default proactive-refresh lead time. The access token lives 24h
// (TOKEN_VALIDITY_SECONDS=86400 on the backend); refreshing when there is
// under an hour left rotates it well before expiry and, because the backend
// reissues a fresh 7-day refresh token on every refresh, keeps the user's
// 7-day sliding window advancing with activity.
export const DEFAULT_REFRESH_BUFFER_MS = 60 * 60 * 1000; // 1 hour

/**
 * Check if the access token should be proactively refreshed.
 * @param {number} bufferMs - Refresh this many ms before expiry (default 1h).
 * @returns {boolean} - True if token should be refreshed
 */
export function tokenNeedsRefresh(bufferMs = DEFAULT_REFRESH_BUFFER_MS) {
  const expiryTimeUTC = getTokenExpiry();

  if (!expiryTimeUTC) {
    console.warn('⚠️ No token expiry time stored');
    return true; // Assume needs refresh if we don't have expiry
  }

  try {
    const expiryTime = new Date(expiryTimeUTC);
    const now = new Date();

    const timeRemaining = expiryTime - now;

    if (timeRemaining < bufferMs) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ Error parsing token expiry:', error);
    return true; // Assume needs refresh on error
  }
}

/**
 * Check if token is expired
 * @returns {boolean} - True if token is expired
 */
export function isTokenExpired() {
  const expiryTimeUTC = getTokenExpiry();

  if (!expiryTimeUTC) {
    return true;
  }

  try {
    const expiryTime = new Date(expiryTimeUTC);
    const now = new Date();
    return now >= expiryTime;
  } catch (error) {
    console.error('❌ Error checking token expiry:', error);
    return true;
  }
}

/**
 * True only when we KNOW the access token is past its expiry: an expiry is
 * stored and it is in the past.
 *
 * Deliberately distinct from {@link isTokenExpired}, which reports true when no
 * expiry is stored at all. "Unknown" must not mean "expired" for the callers
 * that force a logout: passkey logins carry no expiryTimeUTC in their response,
 * so treating a missing expiry as expired would sign those users out on their
 * very first request. This predicate fails open — no expiry, or an unparseable
 * one, means "don't act".
 *
 * @returns {boolean}
 */
export function isTokenKnownExpired() {
  const expiryTimeUTC = getTokenExpiry();
  if (!expiryTimeUTC) return false;
  try {
    const expiryTime = new Date(expiryTimeUTC);
    if (Number.isNaN(expiryTime.getTime())) return false;
    return new Date() >= expiryTime;
  } catch (_) {
    return false;
  }
}

/**
 * Check if user has a specific role
 * @param {string} role - Role to check for (e.g., 'ADMIN')
 * @returns {boolean} - True if user has the role
 */
export function hasRole(role) {
  const userInfo = getUserInfo();
  if (!userInfo || !userInfo.roles) {
    return false;
  }
  return userInfo.roles.includes(role);
}

/**
 * Check if user has admin role
 * @returns {boolean} - True if user is admin
 */
export function isAdmin() {
  return hasRole('ADMIN');
}
